import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, CreditCard, Receipt, ExternalLink, Globe, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Helmet } from '@/components/Helmet';
import { LogoShield } from '@/components/LogoShield';
import { getDomainLabel, getDomainPrice } from '@/components/portal/pricing';
import { CANCELLATION_FEES, type TierType } from '@/components/portal/TierSelection';
import { StatusBadge } from '@/components/portal/StatusBadge';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { useCurrency } from '@/hooks/use-currency';
import { openExternalUrl } from '@/lib/navigation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Invoice {
  id: string;
  number: string;
  date: number;
  amount: number;
  amountPaid?: number;
  amountOwing?: number;
  currency: string;
  status: string;
  description: string;
  hostedUrl: string;
  pdfUrl: string;
}

export default function Billing() {
  const { user, loading } = useAuth();
  const { isImpersonating, activeCustomerId, activeCustomerEmail, activeCustomerName, clearImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const { checking } = useRequireMfa();
  const queryClient = useQueryClient();
  const [billingLoading, setBillingLoading] = useState(false);
  const [resumingDomainId, setResumingDomainId] = useState<string | null>(null);
  const [deletingDomainId, setDeletingDomainId] = useState<string | null>(null);
  const [confirmDomainInput, setConfirmDomainInput] = useState('');
  const [confirmCancelFees, setConfirmCancelFees] = useState<Record<string, boolean>>({});
  const [impersonationForceRemove, setImpersonationForceRemove] = useState<Record<string, boolean>>({});
  const [impersonationForceReason, setImpersonationForceReason] = useState<Record<string, string>>({});
  const { formatCurrency } = useCurrency();
  const isImpersonationView = Boolean(isImpersonating && activeCustomerId);
  const subjectUserId = isImpersonationView ? activeCustomerId : user?.id;
  const isPaymentNeeded = (status: string) =>
    status === 'payment_needed' || status === 'payment_incomplete' || status === 'pending_payment';
  const getTierType = (domain: { stripe_price_id?: string | null; stripePriceId?: string | null; promo_price?: number | null; promoPrice?: number | null; tier?: string | null }): TierType => {
    const label = getDomainLabel(domain).toLowerCase();
    if (label.includes('essential')) return 'essential';
    return 'premium';
  };

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });
  const showAdminAggregates = isAdmin === true && !isImpersonationView;

  // Admin accounts summary
  const { data: adminAccounts } = useQuery({
    queryKey: ['admin-billing-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-list-accounts');
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data?.accounts || [];
    },
    enabled: !!user && showAdminAggregates,
  });

  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains', subjectUserId, isImpersonationView ? 'impersonation' : 'self'],
    queryFn: async () => {
      if (!subjectUserId) return [];
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('user_id', subjectUserId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!subjectUserId,
  });

  const { data: profile } = useQuery({
    queryKey: ['profile-payment-bypass', subjectUserId],
    queryFn: async () => {
      if (!subjectUserId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('payment_lock_bypass')
        .eq('user_id', subjectUserId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!subjectUserId,
  });

  const paymentBypassUser = Boolean(profile?.payment_lock_bypass);

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', subjectUserId, isImpersonationView ? 'impersonation' : 'self'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      
      const { data, error } = await supabase.functions.invoke('list-invoices', {
        body: isImpersonationView && activeCustomerId ? { targetUserId: activeCustomerId } : {},
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      return data?.invoices as Invoice[] || [];
    },
    enabled: !!subjectUserId && !showAdminAggregates,
  });

  const handleSyncSubscription = async () => {
    try {
      await supabase.functions.invoke('sync-subscription-quantity', {
        body: isImpersonationView && activeCustomerId ? { targetUserId: activeCustomerId } : {},
      });
      queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
      queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
      toast.success('Billing refreshed from Stripe.');
    } catch (err: any) {
      console.error('Subscription sync failed:', err);
      toast.error('Could not sync subscription quantity.');
    }
  };

  // NOTE: subscription quantity is now synced by backend flows (add/delete domain). If needed, a manual sync button can call
  // sync-subscription-quantity from a trusted environment to avoid browser CORS issues.

  const handleManageBilling = async () => {
    if (showAdminAggregates) {
      openExternalUrl('https://dashboard.stripe.com/');
      return;
    }
    setBillingLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in again to access billing.');
        navigate('/portal/auth');
        return;
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: isImpersonationView && activeCustomerId ? { targetUserId: activeCustomerId } : {},
      });
      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      if (data?.url) {
        openExternalUrl(data.url);
        if (data.type === 'setup') {
          toast.info('Please add a payment method to continue.');
        }
      }
    } catch (err: any) {
      console.error('Billing portal error:', err);
      toast.error('Unable to open billing portal. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  const formatInvoiceCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const handleResumePayment = async (domainId: string) => {
    setResumingDomainId(domainId);
    try {
      if (isImpersonationView) {
        if (!activeCustomerId) {
          throw new Error('No active customer selected for impersonation.');
        }
        const { data: impersonationContext, error: contextError } = await supabase.functions.invoke('admin-impersonation-context', {
          body: { customerId: activeCustomerId },
        });
        if (contextError || impersonationContext?.error) {
          throw contextError || new Error(impersonationContext?.error || 'Unable to load customer impersonation context.');
        }
        const requestId = impersonationContext?.domains?.find((domain: any) => domain?.id === domainId)?.pending_purchase_request?.id;
        if (!requestId) {
          throw new Error('No pending purchase request was found for this domain.');
        }
        const { data, error } = await supabase.functions.invoke('admin-resend-domain-purchase-request', {
          body: { requestId },
        });
        if (error || data?.error) {
          throw error || new Error(data?.error || 'Unable to resend customer payment request.');
        }
        toast.success('Customer payment email resent.');
        queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
        queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
        queryClient.invalidateQueries({ queryKey: ['admin-impersonation-context', activeCustomerId] });
        return;
      }

      const { data, error } = await supabase.functions.invoke('resume-checkout', {
        body: { domainId },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.assign(data.url);
        toast.success('Redirecting to checkout to complete payment.');
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err: any) {
      console.error('Resume checkout failed:', err);
      toast.error(err?.message || (isImpersonationView ? 'Unable to resend customer payment email.' : 'Unable to resume payment. Please try again.'));
    } finally {
      setResumingDomainId(null);
    }
  };

  const handleDeleteDomain = async (domain: any) => {
    if (confirmDomainInput.trim().toLowerCase() !== domain.domain_name.trim().toLowerCase()) {
      toast.error('Please type the domain name exactly to confirm removal.');
      return;
    }

    if (isImpersonationView) {
      const forceRemove = Boolean(impersonationForceRemove[domain.id]);
      const forceReason = (impersonationForceReason[domain.id] || '').trim();
      if (forceRemove && !forceReason) {
        toast.error('Provide a reason before force removing this domain.');
        return;
      }

      setDeletingDomainId(domain.id);
      try {
        const { data, error } = await supabase.functions.invoke('admin-domain-removal-request', {
          body: {
            action: forceRemove ? 'force-remove' : 'request',
            domainId: domain.id,
            reason: forceRemove ? forceReason : undefined,
          },
        });
        if (error || data?.error) {
          throw error || new Error(data?.error || 'Unable to process domain removal.');
        }
        if (forceRemove) {
          toast.success('Domain force removed. Customer and support were notified.');
        } else {
          toast.success('Confirmation email sent to the customer. No cancellation fee was charged.');
        }
        queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
        queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
      } catch (err: any) {
        console.error('Impersonation delete domain error:', err);
        toast.error(err?.message || 'Failed to process domain removal');
      } finally {
        setDeletingDomainId(null);
        setConfirmDomainInput('');
        setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: false }));
        setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
      }
      return;
    }

    setDeletingDomainId(domain.id);
    try {
      const { error } = await supabase
        .from('domains')
        .update({
          status: 'deleted',
          deleted_at: new Date().toISOString(),
        })
        .eq('id', domain.id);

      if (error) throw error;

      await supabase.functions.invoke('notify-domain-change', {
        body: {
          action: 'deleted',
          domains: [{
            domainName: domain.domain_name,
            wpAdminUrl: domain.wp_admin_url,
            wpUsername: domain.wp_username,
            status: domain.status,
            createdAt: domain.created_at,
          }],
          userEmail: user!.email,
          changes: `Domain "${domain.domain_name}" was deleted by the customer via billing.`,
        },
      });

      queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
      queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
      toast.success('Domain deleted.');
    } catch (err: any) {
      console.error('Delete domain error:', err);
      toast.error('Failed to delete domain');
    } finally {
      setDeletingDomainId(null);
      setConfirmDomainInput('');
    }
  };

  // Derived values (keep before conditional returns to preserve hook order)
  const totalDomains = domains?.length || 0;
  const tierBreakdown = (domains || []).reduce<Record<string, { count: number; price: number; label: string }>>((acc, domain) => {
    const price = getDomainPrice(domain);
    const label = getDomainLabel(domain);
    const key = `${label}-${price}`;
    if (!acc[key]) acc[key] = { count: 0, price, label };
    acc[key].count += 1;
    return acc;
  }, {});

  const monthlyTotal = (domains || []).reduce((sum, domain) => {
    return sum + getDomainPrice(domain);
  }, 0);

  const adminSubscriptions = useMemo(() => {
    if (!isAdmin || !adminAccounts) return [];
    return adminAccounts.map((acct: any) => {
      const byTier = new Map<string, { label: string; count: number; price: number }>();
      (acct.domains || []).forEach((d: any) => {
        const price = getDomainPrice(d);
        const label = getDomainLabel(d);
        const key = `${label}-${price}`;
        const existing = byTier.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          byTier.set(key, { label, count: 1, price });
        }
      });
      return {
        email: acct.email || 'Unknown email',
        fullName: acct.fullName || null,
        breakdown: Array.from(byTier.values()),
      };
    });
  }, [isAdmin, adminAccounts]);

  const filteredInvoices = (invoicesData || []).filter(
    (invoice) => invoice.status !== 'draft' && invoice.status !== 'void'
  );
  const impersonationHomePath = isImpersonationView && activeCustomerId
    ? `/portal/admin/impersonate/${activeCustomerId}`
    : '/portal';

  if (checking || loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Billing & Subscriptions | ShadowScan Portal</title>
          <meta
            name="description"
            content="Review ShadowScan billing details, invoices, and subscription quantities for your protected WordPress domains, and update payment methods securely."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Billing & Subscriptions | ShadowScan Portal</title>
        <meta
          name="description"
          content="View your ShadowScan subscription totals, invoice history, and payment settings for all protected domains, and refresh billing directly from the portal."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(impersonationHomePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {isImpersonationView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                clearImpersonation();
                navigate('/portal/admin');
              }}
            >
              Exit customer view
            </Button>
          )}
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Billing & Invoices</span>
              {isImpersonationView && (
                <span className="text-xs text-[hsl(var(--ss-warning))]">
                  Customer view: {activeCustomerName || activeCustomerEmail || activeCustomerId}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        {/* Billing Summary */}
        <Card className="cyber-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              {showAdminAggregates ? 'Billing Summary (All Users)' : 'Billing Summary'}
            </CardTitle>
            <CardDescription>
              {showAdminAggregates
                ? 'Aggregated totals across all customers.'
                : isImpersonationView
                  ? 'Customer billing view while impersonating. Actions are logged as admin-initiated.'
                : 'Overview of your current subscription and billing. Billed on the 1st of each month.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total Domains</span>
                  <span className="font-mono font-bold">
                    {showAdminAggregates
                      ? (adminAccounts || []).reduce(
                          (sum: number, acct: any) => sum + (acct.domains?.length || 0),
                          0
                        )
                      : totalDomains}
                  </span>
                </div>
                <div className="space-y-2">
                  <span className="text-muted-foreground block">Plan Breakdown</span>
                  {showAdminAggregates ? (
                    <span className="text-sm text-muted-foreground">All customer plans</span>
                  ) : Object.entries(tierBreakdown).length === 0 ? (
                    <span className="text-sm text-muted-foreground">No active domains</span>
                  ) : (
                    Object.entries(tierBreakdown).map(([key, info]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="font-medium">{info.label}</span>
                        <span className="font-mono">
                          {info.count} x {formatCurrency(info.price)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-border">
                  <span className="font-medium">Monthly Total</span>
                  <span className="text-xl font-mono font-bold text-primary">
                    {formatCurrency(
                      showAdminAggregates
                        ? (adminAccounts || []).reduce((sum: number, acct: any) => {
                            return sum + (acct.domains || []).reduce((dSum: number, d: any) => dSum + getDomainPrice(d), 0);
                          }, 0)
                        : monthlyTotal,
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Estimated · Billed in AUD on the 1st</p>
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-sm text-muted-foreground mb-4">
                  {showAdminAggregates
                    ? 'Open the Stripe admin dashboard to manage customer billing, payment methods, and invoices.'
                    : isImpersonationView
                      ? 'Open this customer billing portal while impersonating. Access is audited.'
                    : 'Manage your payment method, view invoices, and update billing details through the Stripe customer portal.'}
                </p>
                <div className="flex gap-2 flex-col sm:flex-row">
                  <Button onClick={handleManageBilling} className="w-full" disabled={billingLoading}>
                    {billingLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    {billingLoading
                      ? 'Loading...'
                      : showAdminAggregates
                      ? 'Open Stripe Dashboard'
                      : isImpersonationView
                        ? 'Open Customer Billing Portal'
                        : 'Manage Payment Method'}
                    {!billingLoading && <ExternalLink className="h-4 w-4 ml-2" />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Section */}
        {!showAdminAggregates && (
          <Card className="cyber-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                Invoice History
              </CardTitle>
              <CardDescription>
                Your recent invoices and payment history.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invoicesLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                  Loading invoices...
                </div>
              ) : filteredInvoices.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between px-4 text-xs uppercase tracking-wide text-muted-foreground">
                    <div className="flex items-center gap-6">
                      <span className="min-w-[100px]">Date</span>
                      <span className="min-w-[80px]">Amount Paid</span>
                      <span className="min-w-[100px]">Amount Owing</span>
                      <span>Status</span>
                      <span className="hidden md:inline">Description</span>
                    </div>
                    <span className="mr-2">Action</span>
                  </div>
                  {filteredInvoices.map((invoice) => (
                    <div 
                      key={invoice.id} 
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                    <div className="flex items-center gap-6">
                      <div className="text-sm text-muted-foreground min-w-[100px]">
                        {format(new Date(invoice.date * 1000), 'd MMM yyyy')}
                      </div>
                      <div className="font-mono font-medium min-w-[80px]">
                        {formatInvoiceCurrency(invoice.amountPaid ?? invoice.amount, invoice.currency)}
                      </div>
                      <div className="font-mono text-sm text-muted-foreground min-w-[100px]">
                        {formatInvoiceCurrency(
                          invoice.amountOwing ?? Math.max((invoice.amount ?? 0) - (invoice.amountPaid ?? 0), 0),
                          invoice.currency
                        )}
                      </div>
                        <StatusBadge status={invoice.status} context="invoice" />
                        <div className="text-sm text-muted-foreground hidden md:block">
                          {invoice.description}
                        </div>
                      </div>
                      {invoice.hostedUrl && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openExternalUrl(invoice.hostedUrl)}
                        >
                          {invoice.status === 'open' ? 'Pay Now' : 'View'}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No invoices yet.</p>
                  <p className="text-sm">Your first invoice will appear after the billing cycle starts.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin Active Subscriptions */}
        {showAdminAggregates && (
          <Card className="cyber-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Active Subscriptions (All Users)
              </CardTitle>
              <CardDescription>
                Breakdown of active subscriptions by customer and plan.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {adminSubscriptions.length === 0 ? (
                <div className="text-muted-foreground text-sm">No active subscriptions found.</div>
              ) : (
                adminSubscriptions.map((acct) => (
                  <Card key={acct.email} className="border-border/50">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="space-y-1">
                          <p className="text-sm font-mono font-semibold">{acct.email}</p>
                          {acct.fullName && (
                            <p className="text-xs text-muted-foreground">{acct.fullName}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {acct.breakdown.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No active plans</p>
                      ) : (
                        acct.breakdown.map((item) => (
                          <div key={`${acct.email}-${item.label}`} className="flex justify-between text-sm">
                            <span>{item.count}x {item.label}</span>
                            <span className="font-mono">{formatCurrency(item.count * item.price)}</span>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Active Subscriptions (non-admin view) */}
        {!showAdminAggregates && (
          <Card className="cyber-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Active Subscriptions
              </CardTitle>
              <CardDescription>
                All domains with monthly billing. Charged on the 1st of each month.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {domainsLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading...</div>
              ) : totalDomains > 0 ? (
                <div className="space-y-3">
                  {domains?.map((domain) => (
                    <div
                      key={domain.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center flex-wrap gap-2 sm:gap-3">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            domain.status === 'active'
                              ? 'bg-[hsl(var(--ss-success))]'
                              : isPaymentNeeded(domain.status)
                                ? 'bg-[hsl(var(--ss-warning))]'
                                : 'bg-[hsl(var(--ss-neutral))]'
                          }`}
                        />
                        <span className="font-mono break-all">{domain.domain_name}</span>
                        <StatusBadge status={domain.status} />
                      </div>
                      <div className="flex items-center gap-3 w-full sm:w-auto sm:ml-auto justify-between sm:justify-end">
                        <span className="text-sm text-muted-foreground flex-1 sm:flex-none">
                          {formatCurrency(getDomainPrice(domain))}/month
                        </span>
                        <div className="flex items-center gap-2 sm:gap-3">
                          {isPaymentNeeded(domain.status) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={resumingDomainId === domain.id}
                                onClick={() => handleResumePayment(domain.id)}
                              >
                                {resumingDomainId === domain.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Opening...
                                  </>
                                ) : (
                                  <>
                                    <span className="hidden sm:inline">{isImpersonationView ? 'Resend Payment Email' : 'Resume Payment'}</span>
                                    <span className="sm:hidden">$</span>
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto sm:ml-0"
                                disabled={deletingDomainId === domain.id}
                              >
                                {deletingDomainId === domain.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove site</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Remove <strong>{domain.domain_name}</strong>? This cancels monitoring and billing for this site.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <div className="py-2 space-y-3">
                                <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                  <div>
                                    Plan:{' '}
                                    <span className="text-foreground">
                                      {getTierType(domain) === 'essential' ? 'Essential' : 'Premium'}
                                    </span>
                                  </div>
                                  <div>
                                    {isImpersonationView ? 'Removal flow:' : 'Cancellation fee:'}{' '}
                                    {isImpersonationView ? (
                                      <span className="text-foreground">
                                        Customer confirmation email (no cancellation fee) by default
                                      </span>
                                    ) : (
                                      <span className="text-foreground">
                                        {formatCurrency(CANCELLATION_FEES[getTierType(domain)])}
                                      </span>
                                    )}
                                  </div>
                                  {isImpersonationView ? (
                                    <div>Force remove is logged and requires a reason.</div>
                                  ) : (
                                    <div>Reason: covers initial setup and configuration.</div>
                                  )}
                                </div>
                                <Label htmlFor={`confirm-${domain.id}`} className="text-sm text-muted-foreground">
                                  Type the domain name to confirm:
                                </Label>
                                <Input
                                  id={`confirm-${domain.id}`}
                                  value={confirmDomainInput}
                                  onChange={(e) => setConfirmDomainInput(e.target.value)}
                                  placeholder={domain.domain_name}
                                  className="mt-2"
                                />
                                {isImpersonationView ? (
                                  <>
                                    <label className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(impersonationForceRemove[domain.id])}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: checked }));
                                          if (!checked) {
                                            setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
                                          }
                                        }}
                                      />
                                      <span>Force remove immediately (skip customer confirmation email).</span>
                                    </label>
                                    {Boolean(impersonationForceRemove[domain.id]) && (
                                      <div>
                                        <Label htmlFor={`force-reason-${domain.id}`} className="text-sm text-muted-foreground">
                                          Force remove reason (required)
                                        </Label>
                                        <Input
                                          id={`force-reason-${domain.id}`}
                                          value={impersonationForceReason[domain.id] || ''}
                                          onChange={(e) => setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: e.target.value }))}
                                          placeholder="Provide the operational reason for immediate removal"
                                          className="mt-2"
                                        />
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <label className="flex items-start gap-2 text-xs text-muted-foreground">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(confirmCancelFees[domain.id])}
                                      onChange={(e) => setConfirmCancelFees((prev) => ({ ...prev, [domain.id]: e.target.checked }))}
                                    />
                                    <span>I understand the cancellation fee will be charged to my payment method.</span>
                                  </label>
                                )}
                              </div>
                              <AlertDialogFooter>
                                <AlertDialogCancel
                                  onClick={() => {
                                    setConfirmDomainInput('');
                                    setConfirmCancelFees((prev) => ({ ...prev, [domain.id]: false }));
                                    setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: false }));
                                    setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
                                  }}
                                >
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteDomain(domain)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  disabled={
                                    isImpersonationView
                                      ? Boolean(impersonationForceRemove[domain.id]) && !(impersonationForceReason[domain.id] || '').trim()
                                      : !confirmCancelFees[domain.id]
                                  }
                                >
                                  {isImpersonationView
                                    ? (impersonationForceRemove[domain.id] ? 'Force remove site' : 'Send confirmation email')
                                    : 'Remove site'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No subscriptions. Add a domain to get started.
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
      <CurrencyDock />
    </div>
  );
}
